import type { LucideIcon } from "lucide-react";
import {
  ActivityIcon,
  AtomIcon,
  AwardIcon,
  BarChart3Icon,
  BellIcon,
  BookIcon,
  BookOpenIcon,
  BrainIcon,
  BriefcaseIcon,
  Building2Icon,
  CalculatorIcon,
  CameraIcon,
  CarIcon,
  ChartColumnIcon,
  ChartLineIcon,
  ClipboardCheckIcon,
  CloudIcon,
  CodeIcon,
  CodeXmlIcon,
  CompassIcon,
  CpuIcon,
  CreditCardIcon,
  DatabaseIcon,
  DropletIcon,
  DumbbellIcon,
  FeatherIcon,
  FilmIcon,
  FlameIcon,
  FlaskConicalIcon,
  FolderIcon,
  FolderTreeIcon,
  GaugeIcon,
  GitBranchIcon,
  GlobeIcon,
  GraduationCapIcon,
  HammerIcon,
  HeartIcon,
  HouseIcon,
  ImageIcon,
  KeyIcon,
  LanguagesIcon,
  LayersIcon,
  LeafIcon,
  LibraryIcon,
  LightbulbIcon,
  LockIcon,
  MapIcon,
  MegaphoneIcon,
  MessageSquareIcon,
  MicIcon,
  MicroscopeIcon,
  MonitorPlayIcon,
  MusicIcon,
  NewspaperIcon,
  PaletteIcon,
  PanelsTopLeftIcon,
  PenLineIcon,
  PenToolIcon,
  PlaneIcon,
  RadioIcon,
  RocketIcon,
  ScaleIcon,
  SearchIcon,
  ServerIcon,
  SettingsIcon,
  ShapesIcon,
  ShieldIcon,
  SigmaIcon,
  SmartphoneIcon,
  SparklesIcon,
  SproutIcon,
  StarIcon,
  StethoscopeIcon,
  SunIcon,
  TagIcon,
  TargetIcon,
  TerminalIcon,
  TrendingUpIcon,
  TrophyIcon,
  UserIcon,
  UsersIcon,
  UtensilsIcon,
  VideoIcon,
  WalletIcon,
  WrenchIcon,
  ZapIcon,
} from "lucide-react";

/**
 * Backend saqlashi MUMKIN bo'lgan ikonkalar ro'yxati.
 *
 * Ilgari bu yerda `lucide-react/dynamic` dan `iconNames` import qilinardi:
 * u Lucide'ning ~1900 nomli butun reyestrini eager bundle'ga tortar, `DynamicIcon`
 * esa har bir ikonka uchun alohida chunk chiqarardi — build `dist/assets` da
 * 1800 dan ortiq fayl bo'lardi.
 *
 * Ro'yxat taksonomiya seed'i (`taxonomy.seed.ts`) ishlatadigan nomlar va
 * kategoriya / daraja formalaridagi fallback'lar ustiga qurilgan; qolganlari
 * admin tanlashi uchun qo'shilgan. YANGI ikonka kerak bo'lsa — shu yerga
 * qo'shiladi, aks holda u `fallback` ga tushadi.
 */
export const ICONS = {
  activity: ActivityIcon,
  atom: AtomIcon,
  award: AwardIcon,
  "bar-chart-3": BarChart3Icon,
  bell: BellIcon,
  book: BookIcon,
  "book-open": BookOpenIcon,
  brain: BrainIcon,
  briefcase: BriefcaseIcon,
  "building-2": Building2Icon,
  calculator: CalculatorIcon,
  camera: CameraIcon,
  car: CarIcon,
  "chart-column": ChartColumnIcon,
  "chart-line": ChartLineIcon,
  "clipboard-check": ClipboardCheckIcon,
  cloud: CloudIcon,
  code: CodeIcon,
  "code-xml": CodeXmlIcon,
  compass: CompassIcon,
  cpu: CpuIcon,
  "credit-card": CreditCardIcon,
  database: DatabaseIcon,
  droplet: DropletIcon,
  dumbbell: DumbbellIcon,
  feather: FeatherIcon,
  film: FilmIcon,
  flame: FlameIcon,
  "flask-conical": FlaskConicalIcon,
  folder: FolderIcon,
  "folder-tree": FolderTreeIcon,
  gauge: GaugeIcon,
  "git-branch": GitBranchIcon,
  globe: GlobeIcon,
  "graduation-cap": GraduationCapIcon,
  hammer: HammerIcon,
  heart: HeartIcon,
  house: HouseIcon,
  image: ImageIcon,
  key: KeyIcon,
  languages: LanguagesIcon,
  layers: LayersIcon,
  leaf: LeafIcon,
  library: LibraryIcon,
  lightbulb: LightbulbIcon,
  lock: LockIcon,
  map: MapIcon,
  megaphone: MegaphoneIcon,
  "message-square": MessageSquareIcon,
  mic: MicIcon,
  microscope: MicroscopeIcon,
  "monitor-play": MonitorPlayIcon,
  music: MusicIcon,
  newspaper: NewspaperIcon,
  palette: PaletteIcon,
  "panels-top-left": PanelsTopLeftIcon,
  "pen-line": PenLineIcon,
  "pen-tool": PenToolIcon,
  plane: PlaneIcon,
  radio: RadioIcon,
  rocket: RocketIcon,
  scale: ScaleIcon,
  search: SearchIcon,
  server: ServerIcon,
  settings: SettingsIcon,
  shapes: ShapesIcon,
  shield: ShieldIcon,
  sigma: SigmaIcon,
  smartphone: SmartphoneIcon,
  sparkles: SparklesIcon,
  sprout: SproutIcon,
  star: StarIcon,
  stethoscope: StethoscopeIcon,
  sun: SunIcon,
  tag: TagIcon,
  target: TargetIcon,
  terminal: TerminalIcon,
  "trending-up": TrendingUpIcon,
  trophy: TrophyIcon,
  user: UserIcon,
  users: UsersIcon,
  utensils: UtensilsIcon,
  video: VideoIcon,
  wallet: WalletIcon,
  wrench: WrenchIcon,
  zap: ZapIcon,
} satisfies Record<string, LucideIcon>;

/** Ruxsat etilgan ikonka nomlari. */
export type AllowedIconName = keyof typeof ICONS;

/** Ro'yxatdagi barcha nomlar — forma va testlar uchun. */
export const ALLOWED_ICON_NAMES = Object.keys(ICONS) as AllowedIconName[];

/** Nom ro'yxatda bormi (backend'dan kelgan ixtiyoriy satr uchun). */
export const isAllowedIconName = (name?: string | null): name is AllowedIconName =>
  // `in` emas: u `toString` kabi meros bo'lib o'tgan xossalarni ham "bor"
  // deb qaytaradi, backend esa ixtiyoriy satr saqlashi mumkin.
  !!name && Object.prototype.hasOwnProperty.call(ICONS, name);

interface IconProps {
  /** Saqlangan ikonka nomi (kebab-case). Noma'lum yoki bo'sh qiymatda `fallback`. */
  name?: string | null;
  /** `name` yo'q yoki ruxsat ro'yxatida bo'lmaganda ko'rsatiladigan ikonka. */
  fallback?: AllowedIconName;
  className?: string;
}

/**
 * Saqlangan kebab-case nomdan Lucide ikonkasini chizadi (masalan, "code-xml",
 * "sprout", "panels-top-left").
 *
 * DIQQAT: dinamik ikonkalarni chizishning ilovadagi yagona umumiy yo'li shu.
 * Kategoriya/daraja ikonkalari uchun ham, kelgusidagi kurs kartochkalari va
 * batafsil sahifalar uchun ham shuni ishlating — ikonka satrlarini qo'lda
 * komponentlarga bog'lamang.
 *
 * @example <Icon name={category.icon} fallback="folder" className="size-5" />
 */
export const Icon = ({ name, fallback = "shapes", className }: IconProps) => {
  const Resolved = isAllowedIconName(name) ? ICONS[name] : ICONS[fallback];

  return <Resolved className={className} />;
};
